import type { ReactNode } from 'react';
import type { Tag } from '../data/menu';
import { cn, useReveal } from '../lib/util';

/* ── Dietary marker — the square veg/egg/non-veg dot used on menus ── */
export function Diet({ tag, className }: { tag: Tag; className?: string }) {
  const color =
    tag === 'veg' ? 'var(--color-leaf)' : tag === 'egg' ? 'var(--color-gold)' : 'var(--color-spice)';
  const label = tag === 'veg' ? 'Vegetarian' : tag === 'egg' ? 'Contains egg' : 'Non-vegetarian';
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn('inline-grid place-items-center shrink-0', className)}
      style={{ width: '0.9rem', height: '0.9rem', border: `1.5px solid ${color}`, borderRadius: 3 }}
    >
      <span style={{ width: '0.4rem', height: '0.4rem', borderRadius: 999, background: color }} />
    </span>
  );
}

/* ── Section heading with kicker + title ── */
export function SectionHead({
  kicker,
  title,
  intro,
  align = 'left',
  className,
}: {
  kicker: string;
  title: ReactNode;
  intro?: ReactNode;
  align?: 'left' | 'center';
  className?: string;
}) {
  return (
    <div
      className={cn(align === 'center' && 'mx-auto text-center', 'max-w-2xl', className)}
    >
      <p className="eyebrow">{kicker}</p>
      <h2
        className="font-display mt-3 text-ink"
        style={{ fontSize: 'clamp(1.9rem, 4.4vw, 3rem)', lineHeight: 1.05 }}
      >
        {title}
      </h2>
      {intro && (
        <p className="mt-4 text-ink-2" style={{ fontSize: '1.05rem', lineHeight: 1.65 }}>
          {intro}
        </p>
      )}
    </div>
  );
}

/* ── Reveal wrapper ── */
export function Reveal({
  children,
  delay = 0,
  className,
  as: As = 'div',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: 'div' | 'li' | 'section';
}) {
  const { ref, shown } = useReveal<HTMLElement>();
  return (
    <As
      ref={ref as never}
      className={cn('reveal', shown && 'in', className)}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </As>
  );
}
